import styles from "./CurrentEmployees.module.css";
import {
  CURRENT_EMPLOYEES_DRAWER_FORM_TITLE,
  NEXT_BUTTON_TEXT,
} from "pages/CensusAndCurrentInformation/utils/constants";
import { useEffect, useState } from "react";
import {
  trackFormError,
  trackPageViewUserStep,
  trackSaveAndContinueUserStep,
} from "utils/analytics/trackEvent";
import { Modal } from "@justworkshr/milo-components";
import CensusDrawer from "uicomponents/CensusDrawer";
import CensusTable from "uicomponents/CensusTable";
import { CurrentEmployeesType } from "types";
import { DependentType, EmployeeType } from "uicomponents/CensusTable/types";
import {
  ParticipantTypeEnum,
  useDeleteDependentCensusMutation,
} from "types/generated/operations";
import { errorMessageMap } from "utils/helper";
import Alert from "uicomponents/Alert";

interface ICurrentEmployeesProps {
  data: CurrentEmployeesType;
  refetchData: () => void;
  setNextStep: () => void;
  setPreviousStep: () => void;
}

interface IDrawerState {
  status: string;
  initialData?: DependentType | EmployeeType;
}

const CurrentEmployees = (props: ICurrentEmployeesProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(3, "Current team", 1);
  }, []);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const [deleteDependentCensus, { loading: deleteDependentCensusLoading }] =
    useDeleteDependentCensusMutation();

  const [drawerState, setDrawerState] = useState<IDrawerState>({
    status: "closed",
    initialData: undefined,
  });

  const { data, setNextStep, setPreviousStep } = props;

  const [existingCensuses, setExistingCensuses] = useState(
    data?.employeeCensuses || []
  );

  useEffect(() => {
    if (data?.employeeCensuses) {
      setExistingCensuses(data.employeeCensuses);
    }
  }, [data]);

  const handleSaveAndContinue = async () => {
    setIsLoadingSteps(true);
    trackSaveAndContinueUserStep(3, "Current team", 1);
    setNextStep();
  };

  const handleDelete = async (uuid: string, participantType?: string) => {
    try {
      if (!uuid) {
        return true;
      }
      await deleteDependentCensus({
        variables: {
          uuid,
          participantType:
            (participantType
              ?.toUpperCase()
              .replace(/-/g, "_") as ParticipantTypeEnum) || "EMPLOYEE",
        },
      });
      props.refetchData();
      return true;
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setErrorMessage(errorMessageMap[errorMessage] || "Please try again");
      trackFormError(error, errorMessage);
      setIsError(true);
      return false;
    }
  };

  const openDrawer = (initialData?: DependentType | EmployeeType) => () => {
    setDrawerState({
      status: "animating",
      initialData,
    });
    setTimeout(() => {
      setDrawerState({
        initialData,
        status: "open",
      });
    }, 10);
  };

  const closeDrawer = (refetch?: boolean) => {
    setDrawerState({
      ...drawerState,
      status: "animating",
    });
    setTimeout(() => {
      setDrawerState({
        status: "closed",
      });
    }, 500);
    if (refetch) props.refetchData();
  };

  return (
    <>
      <Modal
        isOpen={drawerState.status !== "closed"}
        onClose={() => closeDrawer()}
      >
        {drawerState.status !== "closed" && (
          <CensusDrawer
            isOpen={drawerState.status === "open"}
            initialData={drawerState.initialData}
            closeDrawer={closeDrawer}
          />
        )}
      </Modal>
      <div>
        <div className={styles.censusHeading}>
          <h3>{CURRENT_EMPLOYEES_DRAWER_FORM_TITLE}</h3>
          <div className={styles.description}>
            <ul className={styles.descriptionList}>
              <li>Full- and part-time employees (30-hours/week)</li>
              <li>And all dependents</li>
            </ul>
          </div>

          <CensusTable
            censusList={existingCensuses}
            handleBackClick={setPreviousStep}
            handleDelete={handleDelete}
            offerHealthInsurance={
              !!props.data?.healthInsuranceInformation
                ?.currentlyOfferHealthInsurance
            }
            isLoading={deleteDependentCensusLoading || isLoadingSteps}
            nextButtonText={isLoadingSteps ? "Loading" : NEXT_BUTTON_TEXT}
            openDrawer={openDrawer}
            handleSaveAndContinue={handleSaveAndContinue}
          />
          {isError && <Alert message={errorMessage} />}
        </div>
      </div>
    </>
  );
};

export default CurrentEmployees;
